import { bool, fail, isRecord, list, optionalNum, optionalText, sameResource, text } from '../../../shared/api/onecEnvelope'
import type { CashShift, CashShiftState, CashShifts } from '../model/types'

/**
 * Декодер смен 1С (Resource.OneC.Contracts/CashReceipts, `OneCCashShiftsDto`). Каждая ссылка обязана быть про ЭТУ базу (D-064);
 * GUID — в нижнем регистре. Неизвестное состояние — `null` («не понять»), а не отказ всего ответа.
 */
function record(raw: unknown, field: string): Record<string, unknown> {
  return isRecord(raw) ? raw : fail(`${field} — не объект`)
}

function refKey(raw: unknown, field: string, resourceId: string): string {
  const ref = record(raw, field)
  const owner = isRecord(ref.resourceId) ? text(ref.resourceId.value, `${field}.resourceId.value`) : text(ref.resourceId, `${field}.resourceId`)
  if (!sameResource(owner, resourceId)) fail(`${field} ссылается на другую базу`)
  return text(ref.remoteKey, `${field}.remoteKey`).toLowerCase()
}

const optionalRef = (raw: unknown, field: string, resourceId: string): string | null =>
  raw === undefined || raw === null ? null : refKey(raw, field, resourceId)

const stateOf = (raw: unknown): CashShiftState | null => (raw === 'open' || raw === 'closed' ? raw : null)

function shift(raw: unknown, field: string, resourceId: string): CashShift {
  const item = record(raw, field)
  return {
    key: refKey(item.ref, `${field}.ref`, resourceId),
    number: optionalText(item.number, `${field}.number`),
    date: optionalText(item.date, `${field}.date`),
    posted: bool(item.posted, `${field}.posted`),
    registerKey: optionalRef(item.register, `${field}.register`, resourceId),
    registerName: optionalText(item.registerName, `${field}.registerName`),
    shiftNumber: optionalText(item.shiftNumber, `${field}.shiftNumber`),
    openedAt: optionalText(item.openedAt, `${field}.openedAt`),
    closedAt: optionalText(item.closedAt, `${field}.closedAt`),
    state: stateOf(item.state),
    status: optionalText(item.status, `${field}.status`),
    cashierKey: optionalRef(item.cashier, `${field}.cashier`, resourceId),
    cashierName: optionalText(item.cashierName, `${field}.cashierName`),
    receiptCount: optionalNum(item.receiptCount, `${field}.receiptCount`),
    income: optionalNum(item.income, `${field}.income`),
    refunds: optionalNum(item.refunds, `${field}.refunds`),
  }
}

export function decodeCashShifts(resourceId: string) {
  return (raw: unknown): CashShifts => {
    const data = record(raw, 'data')
    return {
      items: list(data.items, 'items', (entry, index) => shift(entry, `items[${index}]`, resourceId)),
      from: text(data.from, 'from'),
      to: text(data.to, 'to'),
      truncated: bool(data.truncated, 'truncated'),
      observedAt: text(data.observedAt, 'observedAt'),
    }
  }
}
